import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { formatTime, formatCurrency, getStatusBadgeClass } from '../../utils/formatters';
import { ChevronLeft, ChevronRight, MapPin, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, isSameMonth } from 'date-fns';

// Parse date string as local date (not UTC)
function parseLocalDate(dateStr) {
  // Handle ISO strings by extracting just the date part
  const datePart = dateStr.substring(0, 10); // "2025-01-15"
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Format date as local (not UTC)
function formatDateLocal(dateStr, formatStr = 'EEEE, MMMM d') {
  const date = parseLocalDate(dateStr);
  return format(date, formatStr);
}

export default function ContractorCalendar() {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const start = subMonths(startOfMonth(currentMonth), 1);
    const end = addMonths(endOfMonth(currentMonth), 1);
    
    api.get(`/contractors/${user.id}/gigs?startDate=${start.toISOString()}&endDate=${end.toISOString()}`)
      .then(setGigs)
      .finally(() => setLoading(false));
  }, [currentMonth, user.id]);

  // Filter gigs for this month AND exclude DRAFT gigs
  // DRAFT gigs are invisible to contractors - they're internal planning only
  const monthGigs = gigs
    .filter(g => g.gigStatus !== 'DRAFT') // <-- CRITICAL: Hide draft gigs from contractors
    .filter(g => isSameMonth(parseLocalDate(g.date), currentMonth));

  // Group by date
  const grouped = monthGigs.reduce((acc, gig) => {
    const key = gig.date.substring(0, 10); // Use just the date part as key
    if (!acc[key]) acc[key] = [];
    acc[key].push(gig);
    return acc;
  }, {});

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="btn-ghost p-2">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold">{format(currentMonth, 'MMMM yyyy')}</h1>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="btn-ghost p-2">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
        </div>
      ) : monthGigs.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <p className="text-gray-500">No gigs this month</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).sort().map(([dateKey, dayGigs]) => (
            <div key={dateKey}>
              <h2 className="text-sm font-medium text-gray-500 mb-2">
                {formatDateLocal(dateKey, 'EEEE, MMMM d')}
              </h2>
              <div className="space-y-2">
                {dayGigs.map(gig => (
                  <Link key={gig.assignmentId} to={`/my/gig/${gig.id}`} className="block card hover:shadow-md transition-shadow">
                    <div className="card-body py-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{gig.venue} - {gig.location}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatTime(gig.startTime)} - {formatTime(gig.endTime)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={getStatusBadgeClass(gig.status)}>{gig.status}</span>
                          <p className="text-sm font-semibold text-green-600 mt-1">{formatCurrency(gig.payout)}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
